import React from 'react';
import './rush.css';
import './legal.css';
import { RushNav, RushFooter, useRushReveal } from './RushChrome';
import { LegalPage } from './LegalPage';

const SUBPROCESSORS = [
  ['Amazon Web Services (AWS)', 'Cloud hosting, primary infrastructure', 'Frankfurt, DE · Dublin, IE'],
  ['Cloudflare', 'CDN, DDoS protection, edge caching', 'Global'],
  ['Stripe', 'Payment processing', 'Dublin, IE'],
  ['Postmark', 'Transactional email delivery', 'Boston, US'],
  ['Linear', 'Internal issue tracking (no customer data)', 'San Francisco, US'],
  ['DeepL', 'AI machine translation (selected languages)', 'Cologne, DE'],
  ['Google Cloud Translation', 'AI machine translation', 'EU + US regions'],
  ['OpenAI', 'AI machine translation', 'San Francisco, US'],
  ['Anthropic', 'AI machine translation', 'San Francisco, US'],
  ['Microsoft Translator', 'AI machine translation', 'Multiple EU regions'],
  ['Amazon Translate', 'AI machine translation', 'EU regions'],
];

const SECTIONS = [
  { id: 'summary', title: 'Summary', body: (
    <>
      <p>This page consolidates everything Rush Studio does to comply with the EU General Data Protection Regulation (GDPR), the UK GDPR, and equivalent data-protection laws elsewhere. It also serves as our standard Data Processing Addendum (DPA) — see <a href="#dpa">section 9</a> for the contract terms.</p>
      <div className="legal-callout"><strong>If you need a signed DPA</strong> in PDF form, email <a href="mailto:privacy@rush-studio.com">privacy@rush-studio.com</a> with your company details. Standard turnaround: 1 business day.</div>
    </>
  ) },
  { id: 'roles', title: "Who's the controller, who's the processor", body: (
    <>
      <p>Under GDPR terminology:</p>
      <ul>
        <li><b>For your own account data</b> (name, email, billing) — Rush Studio is the <strong>controller</strong>. We decide what data to collect and why.</li>
        <li><b>For the content you put into Rush Studio</b> (your files, segments, glossaries, TM) — Rush Studio is the <strong>processor</strong>. You're the controller; we act on your instructions.</li>
      </ul>
      <p>This page covers both relationships. The contract terms in <a href="#dpa">section 9</a> govern us as your processor.</p>
    </>
  ) },
  { id: 'lawful-basis', title: 'Lawful basis', body: (
    <>
      <p>We rely on these GDPR Article 6 bases, depending on the activity:</p>
      <ul>
        <li><b>Contract (Art. 6(1)(b))</b> — running the Service for you.</li>
        <li><b>Legitimate interest (Art. 6(1)(f))</b> — security, fraud prevention, product improvement, marketing to existing customers.</li>
        <li><b>Consent (Art. 6(1)(a))</b> — optional features, prospect marketing.</li>
        <li><b>Legal obligation (Art. 6(1)(c))</b> — tax, audit, regulatory.</li>
      </ul>
    </>
  ) },
  { id: 'data-categories', title: 'Categories of personal data', body: (
    <>
      <p>Rush Studio handles the following categories of personal data:</p>
      <ul>
        <li><b>Account data</b> — name, work email, company, role, password hash, profile preferences.</li>
        <li><b>Authentication data</b> — login timestamps, IP, device, MFA tokens.</li>
        <li><b>Customer content</b> — files, segments, translation memory, glossaries, comments. May contain personal data depending on what you upload.</li>
        <li><b>Usage data</b> — feature usage, error reports, session duration.</li>
        <li><b>Billing data</b> — company name, billing address, VAT number, payment method (we don't store full card numbers; Stripe does).</li>
        <li><b>Support data</b> — emails and tickets you send to us.</li>
      </ul>
    </>
  ) },
  { id: 'subjects', title: 'Data subjects', body: (
    <>
      <p>The people whose personal data we may process include:</p>
      <ul>
        <li><b>Your team</b> — colleagues you invite to your workspace.</li>
        <li><b>Your reviewers and translators</b> — freelancers or agencies you assign work to.</li>
        <li><b>Individuals named in your content</b> — anyone mentioned in files you upload (employees, customers, third parties).</li>
      </ul>
      <p>If you upload content containing personal data, <strong>you</strong> are responsible for ensuring you have a lawful basis to share it with us. We process it under your instructions, per the DPA.</p>
    </>
  ) },
  { id: 'subprocessors', title: 'Sub-processors', body: (
    <>
      <p>We use the following sub-processors to deliver the Service. We have a written data-processing agreement with each one. We give 30 days' notice before adding or replacing any of them; you can object via <a href="mailto:privacy@rush-studio.com">privacy@rush-studio.com</a>.</p>
      <div className="legal-table">
        <div className="legal-table-row head">
          <span>Sub-processor</span><span>Purpose</span><span>Location</span>
        </div>
        {SUBPROCESSORS.map(([name, purpose, location]) => (
          <div className="legal-table-row" key={name}>
            <span><strong>{name}</strong></span>
            <span>{purpose}</span>
            <span className="legal-region">{location}</span>
          </div>
        ))}
      </div>
      <p>For AI machine-translation sub-processors specifically: we send only the segment being translated, plus any glossary terms. No account info, no file context. All providers contractually agree not to train on your data and to delete content after processing.</p>
    </>
  ) },
  { id: 'transfers', title: 'International transfers', body: (
    <>
      <p>Primary data storage stays in the European Union. Some sub-processors (notably US-based AI providers and our email delivery service) involve transferring personal data outside the EU/UK.</p>
      <p>For these transfers we rely on:</p>
      <ul>
        <li><b>Standard Contractual Clauses (SCCs)</b> approved by the European Commission, with the 2021 revisions.</li>
        <li><b>UK International Data Transfer Addendum</b> for UK-origin data.</li>
        <li><b>EU-US Data Privacy Framework</b> for sub-processors certified under it (currently AWS, OpenAI, Anthropic).</li>
      </ul>
      <p>Transfer impact assessments are available under NDA — email <a href="mailto:privacy@rush-studio.com">privacy@rush-studio.com</a>.</p>
    </>
  ) },
  { id: 'rights', title: 'Data subject rights', body: (
    <>
      <p>Under GDPR, individuals have the right to access, rectify, erase, restrict, port, and object to processing of their personal data, plus the right to withdraw consent and to lodge a complaint with a supervisory authority.</p>
      <p>If you're our direct customer: most rights are exercisable from your account settings. For anything else, email <a href="mailto:privacy@rush-studio.com">privacy@rush-studio.com</a>; we respond within 30 days.</p>
      <p>If you're not our customer but your data is in a customer's workspace: we'll route your request to the relevant customer (the controller). Their privacy policy will apply.</p>
    </>
  ) },
  { id: 'dpa', title: 'Data Processing Addendum (DPA)', body: (
    <>
      <p>This section is our standard DPA, which forms part of your Terms of Service when you use Rush Studio with personal data.</p>
      <p><strong>9.1 Scope.</strong> We act as a processor on your behalf in respect of the customer content you upload to the Service. We process that content only on your documented instructions, as set out in these Terms and the Service's documentation.</p>
      <p><strong>9.2 Confidentiality.</strong> We ensure that personnel authorized to process your data are bound by confidentiality obligations.</p>
      <p><strong>9.3 Security.</strong> We implement the technical and organizational measures described in <a href="#security">section 11</a>, which we consider appropriate to the risk.</p>
      <p><strong>9.4 Sub-processors.</strong> You give us general authorization to engage sub-processors. The current list is in <a href="#subprocessors">section 6</a>. We will give 30 days' notice before changes and impose equivalent obligations on each sub-processor.</p>
      <p><strong>9.5 Data subject requests.</strong> We will assist you in responding to data subject requests, taking into account the nature of the processing. We will forward to you any request we receive directly that relates to your content.</p>
      <p><strong>9.6 Incidents.</strong> We will notify you of a personal data breach without undue delay, and in any event within 72 hours of becoming aware of it. See <a href="#incidents">section 10</a>.</p>
      <p><strong>9.7 Audits.</strong> Once per year, you may audit our compliance with this DPA, subject to reasonable notice and confidentiality. SOC 2 and similar reports are accepted in lieu of on-site audit for most cases.</p>
      <p><strong>9.8 Return / deletion.</strong> On termination, we will delete or return your customer content within 30 days, subject to retention required by law.</p>
      <p><strong>9.9 International transfers.</strong> Where transfers outside the EEA occur, the SCCs in the EU Commission Implementing Decision 2021/914 apply, with the modules and options indicated in our standard DPA exhibit. <a href="mailto:privacy@rush-studio.com">Request the exhibit</a> for the formal copy.</p>
    </>
  ) },
  { id: 'incidents', title: 'Incident response', body: (
    <>
      <p>If a security incident affects your data, we notify the affected customers without undue delay, and in any case within 72 hours of becoming aware of it. The notice includes:</p>
      <ul>
        <li>The nature of the incident and the categories of data affected.</li>
        <li>The approximate number of data subjects affected.</li>
        <li>The likely consequences.</li>
        <li>The measures we've taken or propose to mitigate it.</li>
        <li>The contact at Rush Studio you can ask follow-up questions of.</li>
      </ul>
      <p>Major incidents are also disclosed publicly on our status page within 24 hours.</p>
    </>
  ) },
  { id: 'security', title: 'Security measures', body: (
    <>
      <p>Our technical and organizational measures include (non-exhaustive):</p>
      <ul>
        <li>TLS 1.3 encryption in transit; AES-256 at rest.</li>
        <li>Role-based access control with least-privilege defaults.</li>
        <li>Mandatory MFA for all production access.</li>
        <li>Centralized audit logging, retained for 13 months.</li>
        <li>Annual penetration test by an external vendor (latest report available under NDA).</li>
        <li>SOC 2 Type II certified, ISO 27001 in progress.</li>
        <li>Daily encrypted backups with cross-region replication, 30-day point-in-time recovery.</li>
        <li>Quarterly disaster recovery drills.</li>
        <li>Security training for all employees on hire and annually.</li>
      </ul>
    </>
  ) },
  { id: 'dpo', title: 'Data Protection Officer', body: (
    <>
      <p>Rush Studio has appointed a Data Protection Officer (DPO) as required by GDPR Article 37 (we handle personal data at scale and so are required to appoint one).</p>
      <p><strong>DPO:</strong> Pedro Almeida<br /><strong>Email:</strong> <a href="mailto:privacy@rush-studio.com">privacy@rush-studio.com</a><br /><strong>Postal:</strong> Rush Studio Ltd., 12 Hatton Garden, London EC1N 8AT, UK</p>
      <p>Our EU representative under GDPR Article 27 is appointed in Berlin, contactable via the same DPO email.</p>
    </>
  ) },
];

const RushGdprPage = () => {
  useRushReveal();
  return (
    <div className="rs">
      <RushNav />
      <LegalPage
        eyebrow="Legal · GDPR"
        title="GDPR & Data Processing Addendum"
        updated="May 20, 2026"
        intro="How Rush Studio complies with GDPR, the UK GDPR, and equivalent regulations — including our sub-processor list, lawful bases, international transfer mechanisms, and standard DPA."
        sections={SECTIONS}
        related={[
          { href: '/privacy', label: 'Privacy Policy' },
          { href: '/terms', label: 'Terms of Service' },
          { href: '/contact', label: 'Contact us' },
        ]}
      />
      <RushFooter />
    </div>
  );
};

export default RushGdprPage;
